import { Grid } from '@mui/material'
import Header from '../../components/Header/Header'
import './PointsManagementPage.scss'
import { useState, useEffect } from 'react'
import merchantApi from '../../services/merchantApi'

export default function PointsManagementPage() {
    const [formObject, setFormObject] = useState({
        point: '',
        minimum_spend: '',
        expiry_policy: 'no_expiry',
        point_expiry_period: '0',
        expiry_unit: 'day',
    })
    const [userInfo, setUserInfo] = useState(null)
    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)
    const [notice, setNotice] = useState('')
    const [apiError, setApiError] = useState('')
    const [fieldError, setFieldError] = useState({})

    useEffect(() => {
        loadUsers()
        loadPointsData()
    }, [])

    const loadUsers = async () => {

        try {
            const response = await merchantApi.auth.me()
            setUserInfo(response?.data || null);
        } catch (err) {
            setUserInfo(null)
        } finally {
            setLoading(false)
        }
    }

    const loadPointsData = async () => {
        try {
            const response = await merchantApi.pointsManagement.show()
            setFormObject({
                dollar: response?.data?.dollar || 1,
                point: response?.data?.point || '',
                minimum_spend: response?.data?.minimum_spend || '',
                expiry_policy: response?.data?.expiry_policy || 'no_expiry',
                point_expiry_period: response?.data?.point_expiry_period || '0',
                expiry_unit: response?.data?.expiry_unit || 'day',
            })
        } catch (err) {
            console.error(err)
        }
    }

    const handleSubmit = async () => {
        if (loading || submitting) return

        setSubmitting(true)
        setNotice('')
        setApiError('')

        let payload = {
            dollar: formObject.dollar,
            point: formObject.point,
            minimum_spend: formObject.minimum_spend,
            expiry_policy: formObject.expiry_policy,
            point_expiry_period: formObject.point_expiry_period,
            expiry_unit: formObject.expiry_unit,
        }
        try {
            const response = await merchantApi.pointsManagement.update(payload)
            await loadPointsData()

            setNotice(response?.message || 'Points settings updated successfully.')
            setFieldError({})
            setApiError('')
        } catch (err) {
            const errors = err?.data?.errors || {}

            setFieldError(errors)
            setApiError(err?.message || 'Unable to update points settings.')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div className="points-page">
            <Header
                items={[
                    { label: userInfo?.merchant?.business_name || 'Merchant', to: '/' },
                    { label: 'Points management', current: true },
                ]}
            />

            <div className="points-page__heading">
                <div>
                    <h1 className="points-page__title">Points management</h1>
                    <p className="points-page__subtitle">
                        Earning, expiry and outlet-level configuration
                    </p>
                </div>

                <button type="button" className="points-page__save" onClick={handleSubmit} disabled={loading || submitting}>
                    {submitting ? 'Saving...' : 'Save changes'}
                </button>
            </div>

            {notice && <div className="points-message points-message--success">{notice}</div>}
            {apiError && <div className="points-message points-message--error">{apiError}</div>}
            <section className="points-card">
                <div className="points-card__header">Earning rate</div>
                <div className="points-card__body">
                    <div className="points-form-grid">
                        <div className="points-field">
                            <label className="points-field__label" htmlFor="points-per-ringgit">
                                Points per RM spent
                            </label>

                            <div className="points-prefix-input">
                                <input
                                    id="points-per-ringgit"
                                    type="text"
                                    inputMode="numeric"
                                    disabled={loading || submitting}
                                    value={formObject.point}
                                    onChange={(e) => setFormObject({ ...formObject, point: e.target.value })}
                                />
                                <span className="points-prefix-input__suffix">pts / RM {formObject.dollar}</span>
                            </div>

                            <p className="points-field__hint">
                                Customer earns {formObject.point} pts per RM{formObject.dollar} spent
                            </p>

                            {fieldError?.point && <span className="points-field__error">{fieldError?.point}</span>}
                        </div>

                        <div className="points-field">
                            <label className="points-field__label" htmlFor="minimum-spend">
                                Min spend to earn
                            </label>

                            <div className="points-prefix-input">
                                <span className="points-prefix-input__tag">RM</span>
                                <input
                                    id="minimum-spend"
                                    type="text"
                                    inputMode="decimal"
                                    value={formObject.minimum_spend}
                                    onChange={(e) => setFormObject({ ...formObject, minimum_spend: e.target.value })}
                                    disabled={loading || submitting}
                                />
                            </div>
                            {fieldError?.minimum_spend && <span className="points-field__error">{fieldError?.minimum_spend}</span>}
                        </div>
                    </div>
                </div>
            </section>

            <section className="points-card">
                <div className="points-card__header">Expiry policy</div>
                <div className="points-card__body">
                    <Grid container spacing={2}>
                        <Grid item sm={12}>
                            <button type="button" className={`points-expiry-option ${formObject.expiry_policy === 'no_expiry' ? 'points-expiry-option--active' : ''}`} onClick={() => setFormObject({ ...formObject, expiry_policy: 'no_expiry' })}>
                                <span className="points-expiry-option__radio" />
                                <span className="points-expiry-option__body">
                                    <strong>Permanent — no expiry</strong>
                                    <span>Points never expire. Best for long-term loyalty.</span>
                                </span>
                            </button>
                        </Grid>
                        <Grid item sm={12}>
                            <button type="button" className={`points-expiry-option ${formObject.expiry_policy === 'expire_after' ? 'points-expiry-option--active' : ''}`} onClick={() => setFormObject({ ...formObject, expiry_policy: 'expire_after' })}>
                                <span className="points-expiry-option__radio" />
                                <span className="points-expiry-option__body">
                                    <strong>Expiring points</strong>
                                    <span>Points expire after a set period from when earned.</span>
                                    {
                                        formObject.expiry_policy === 'expire_after' &&
                                        <Grid container spacing={2} width={'100%'} mt={0.5}>
                                            <Grid item sm={6} xs={12}>
                                                <div className="points-field">
                                                    <label className="points-field__label" htmlFor="expire-after">
                                                        Expire after
                                                    </label>
                                                    <div className="points-expiry-input">
                                                        <input
                                                            id="expire-after"
                                                            type="text"
                                                            inputMode="numeric"
                                                            aria-label="Expiry duration"
                                                            value={formObject.point_expiry_period}
                                                            onChange={(e) => setFormObject({ ...formObject, point_expiry_period: e.target.value })}
                                                        />
                                                        <select value={formObject.expiry_unit} onChange={(e) => setFormObject({ ...formObject, expiry_unit: e.target.value })} aria-label="Expiry duration unit">
                                                            <option value="day">days</option>
                                                            <option value="month">months</option>
                                                            {/* <option value="years">years</option> */}
                                                        </select>
                                                    </div>
                                                    {fieldError?.point_expiry_period && <div className="points-field__error">{fieldError?.point_expiry_period}</div>}
                                                </div>
                                            </Grid>
                                            {/* <Grid item sm={6} xs={12}>
                                                <div className="points-field">
                                                    <label className="points-field__label" htmlFor="notify-before">
                                                        Notify customer before
                                                    </label>
                                                    <div className="points-prefix-input">
                                                        <input
                                                            id="notify-before"
                                                            type="text"
                                                            inputMode="numeric"
                                                            value={formObject.notify_before_period}
                                                            onChange={(e) => setFormObject({ ...formObject, notify_before_period: e.target.value })}
                                                        />
                                                        <span className="points-prefix-input__suffix">days</span>
                                                    </div>
                                                </div>
                                            </Grid> */}
                                        </Grid>
                                    }
                                </span>
                            </button>
                        </Grid>
                        <Grid item sm={12}>
                            <button type="button" className={`points-expiry-option ${formObject.expiry_policy === 'reset_on_activity' ? 'points-expiry-option--active' : ''}`} onClick={() => setFormObject({ ...formObject, expiry_policy: 'reset_on_activity' })}>
                                <span className="points-expiry-option__radio" />
                                <span className="points-expiry-option__body">
                                    <strong>Reset on activity</strong>
                                    <span>Balance resets when customer redeems a reward.</span>
                                    {
                                        formObject.expiry_policy === 'reset_on_activity' &&
                                        <Grid container spacing={2} width={'100%'} mt={0.5}>
                                            <Grid item sm={6} xs={12}>
                                                <div className="points-field">
                                                    <label className="points-field__label" htmlFor="expire-after">
                                                        Expire after
                                                    </label>
                                                    <div className="points-expiry-input">
                                                        <input
                                                            id="expire-after"
                                                            type="text"
                                                            inputMode="numeric"
                                                            aria-label="Expiry duration"
                                                            value={formObject.point_expiry_period}
                                                            onChange={(e) => setFormObject({ ...formObject, point_expiry_period: e.target.value })}
                                                        />
                                                        <select
                                                            value={formObject.expiry_unit}
                                                            onChange={(e) => setFormObject({ ...formObject, expiry_unit: e.target.value })}
                                                            aria-label="Expiry duration unit"
                                                        >
                                                            <option value="day">days</option>
                                                            <option value="month">months</option>
                                                            {/* <option value="year">years</option> */}
                                                        </select>
                                                        {fieldError?.point_expiry_period && <div className="points-field__error">{fieldError?.point_expiry_period}</div>}
                                                    </div>
                                                </div>
                                            </Grid>
                                        </Grid>
                                    }
                                </span>
                            </button>
                        </Grid>
                    </Grid>
                </div>
            </section>
        </div>
    )
}
